import { PayloadAction } from '@reduxjs/toolkit';
import { findContentForStageSaga } from 'features/layout/sagas/findContentForStageSaga';
import { broadcasterLimitReachedNotificationUpdateRequested } from 'features/notifications/actions';
import { User, UserId } from 'features/users/types';
import { selectActiveUserById, selectLocalUserId } from 'features/users/usersSlice';
import { call, put, select } from 'redux-saga/effects';
import { logger } from 'utils/logger';
import { RTCClient } from 'utils/webrtc';
import { extractMediaStreamState } from 'utils/webrtc/diagnostics/streamStateExtractor';
import { StreamingStarted } from '../types';

export function* onStreamingStartSaga(action: PayloadAction<StreamingStarted>) {
  const { id: userId, feedId, streams } = action.payload;
  const localUserId: UserId = yield select(selectLocalUserId);

  // Guard: skip subscription for feeds whose publisher is no longer in the room.
  // This prevents subscribing to stale feeds from race conditions where a streamStarted
  // signaling event arrives after the publisher has already left/unpublished.
  if (userId !== localUserId) {
    const user: User | undefined = yield select(selectActiveUserById, userId);

    if (!user) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn(
          `[STALE-FEED] Ignoring streamStarted for feed ${feedId} — user ${userId} is not an active participant. Likely a stale signaling event.`,
          { userId, feedId, streams }
        );
      return;
    }
  }

  // Log health check for remote audio streams only (skip local user's streams and video)
  // Video streams don't exist yet at this point, so logging them provides no value
  if (userId !== localUserId && streams.length > 0) {
    const audioStreams = streams.filter((s) => s.type === 'audio');
    if (audioStreams.length > 0) {
      const feedStreams = RTCClient.receivingFeed.media.getFeedStreams(feedId);
      audioStreams.forEach((stream) => {
        const existingStream = feedStreams.streams[stream.mid];
        const audioElement = existingStream?.subscriberMid
          ? RTCClient.receivingFeed.audioElements[existingStream.subscriberMid]
          : undefined;

        const streamState = extractMediaStreamState(
          feedId,
          existingStream?.subscriberMid || '',
          existingStream,
          audioElement
        );

        logger
          .remote({ system: true, capture: 'streaming' })
          .debug(`[STREAM-HEALTH] streaming-start received for audio`, {
            userId,
            ...streamState,
          });
      });
    }
  }

  yield call(RTCClient.receivingFeed.addAvailableStreams, feedId, streams);

  yield call(RTCClient.setBroadcastingState, userId, streams);

  yield call(RTCClient.subscribe, action.payload);

  // @TODO: we then call this second time when order changes, which may be too much.
  // Think about better solutions
  yield call(RTCClient.receivingFeed.updateSimulcastConfig);

  if (userId !== localUserId) {
    yield put(broadcasterLimitReachedNotificationUpdateRequested());
  }

  yield call(findContentForStageSaga);
}
